import { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Checkbox, Typography } from '@remarkable/ark-web';
import * as Sentry from '@sentry/react';
import {
  CheckoutElementsProvider,
  PaymentElement,
} from '@stripe/react-stripe-js/checkout';
import type { StripeError } from '@stripe/stripe-js';
import { useForm } from 'react-hook-form';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CheckoutSession } from 'src/api/endpoints/cloudConnect.types';
import {
  Button,
  Form,
  NotificationBox,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { LinkExternal } from 'src/components/LinkExternal';
import { LogCategory } from 'src/utils/logger.categories';
import {
  appearance,
  paymentElementOptions,
  useStripeClient,
} from 'src/utils/stripe';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { useCheckoutForm } from '../checkoutFormStore';
import type { CheckoutFormState } from '../components/Checkout.types';
import { usePayment } from './useCheckout';

const TermsAndConditionsLink = () => (
  <LinkExternal
    to={SUPPORT_URL.LEGAL_OVERVIEW}
    inline
    componentLocation={ComponentLocations.CHECKOUT.CONNECT_CHECKOUT}
  >
    <Trans>Terms and Conditions</Trans>
  </LinkExternal>
);

export function PaymentForm({
  checkoutSession,
}: {
  checkoutSession: CheckoutSession;
}) {
  const stripe = useStripeClient(checkoutSession.publishableKey);

  if (stripe.isLoading) {
    return <Spinner />;
  }

  if (stripe.isError || !stripe.data) {
    return <SomethingWentWrong error={stripe.error} />;
  }

  return (
    <CheckoutElementsProvider
      stripe={stripe.data}
      options={{
        clientSecret: checkoutSession.clientSecret,
        // Match the appearance used by the existing (V1) checkout.
        elementsOptions: { appearance },
      }}
    >
      <PaymentFormInner checkoutSession={checkoutSession} />
    </CheckoutElementsProvider>
  );
}

function PaymentFormInner({
  checkoutSession,
}: {
  checkoutSession: CheckoutSession;
}) {
  const { t } = useLingui();
  // Stripe's mocked elements never fire the real 'ready' event in tests, so
  // bypass the gate there - mirrors the same convention in useStripe.ts (V1).
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(
    !!import.meta.env.IS_TEST || false
  );
  const [loadError, setLoadError] = useState<StripeError>();
  const [checkoutFormState, setCheckoutFormState] = useCheckoutForm();
  const form = useForm<Partial<CheckoutFormState>>({
    defaultValues: { ...checkoutFormState },
  });
  const payment = usePayment(checkoutSession);
  const hasAcceptedTerms = form.watch('hasAcceptedTerms');

  return (
    // browser translation breaks the checkout form
    <div translate="no">
      <Form
        {...form}
        onSubmit={(data) => {
          const newState = {
            ...checkoutFormState,
            ...data,
          };
          tracker.trackEvent(
            new ButtonClicked({
              component_location: ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
              text: `Submit payment form`,
              action: `submit payment form`,
            })
          );
          setCheckoutFormState(newState);
          payment.mutate();
        }}
      >
        <div className="flex flex-col gap-24">
          <PaymentElement
            options={paymentElementOptions}
            onReady={() => {
              Sentry.addBreadcrumb({
                category: LogCategory.Stripe,
                message: 'Payment element ready',
              });
              setIsPaymentElementReady(true);
            }}
            onLoadError={(event) => {
              Sentry.captureException(event.error, {
                tags: {
                  context: 'Failed to load payment element.',
                },
              });
              Sentry.addBreadcrumb({
                category: LogCategory.Stripe,
                message: 'Payment element failed to load',
                data: {
                  error: event.error,
                },
              });
              setLoadError(event.error);
            }}
          />
          <div className="flex items-start gap-8">
            <Checkbox
              data-cy="accept-terms-and-conditions"
              id="terms"
              required
              {...form.register('hasAcceptedTerms')}
              onClick={() => {
                tracker.trackEvent(
                  new ButtonClicked({
                    component_location:
                      ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
                    text: `Accept terms and conditions checkbox`,
                    action: `toggle accept TOC checkbox ${
                      hasAcceptedTerms ? 'off' : 'on'
                    }`,
                  })
                );
              }}
            ></Checkbox>
            <Typography variant="body-sm" className="my-0">
              <Trans>
                By subscribing you accept the <TermsAndConditionsLink /> for
                Connect.
              </Trans>
            </Typography>
          </div>
          {loadError && (
            <NotificationBox
              data-cy="payment-element-error"
              variant="error"
              title={t`Something went wrong`}
              className="my-16"
            >
              <Trans>
                We couldn&apos;t load the payment form. Please refresh the page
                and try again.
              </Trans>
            </NotificationBox>
          )}
          {payment.isError && (
            <NotificationBox
              data-cy="checkout-error-box"
              variant="error"
              title={t`Something went wrong during checkout`}
              className="my-16"
              error={payment.error}
            >
              <Trans>
                Please check your information and try again. If the problem
                persists, please contact support.
              </Trans>
            </NotificationBox>
          )}
          <Button
            type="submit"
            data-cy="activate-connect-button"
            variant="primary"
            className="w-full ll:w-fit"
            disabled={!isPaymentElementReady}
            loading={
              form.formState.isSubmitting ||
              payment.isPending ||
              !isPaymentElementReady
            }
            analytics={{
              location: ComponentLocations.CHECKOUT.CONNECT_CHECKOUT,
              text: t`Subscribe`,
              action: 'submit payment information',
            }}
          >
            {t`Subscribe`}
          </Button>
        </div>
      </Form>
    </div>
  );
}
